import { useQuery } from "@tanstack/react-query";
import { Check, Download, FileText, Loader2, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ErrorBanner } from "@/components/common/ErrorBanner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, type EditRow } from "@/lib/api";
import { displayDocumentName } from "@/lib/documentName";
import type { CitationObject, EditAnnotation } from "@/lib/stream";
import { DocView } from "@/components/shared/DocView";
import { DocxView } from "@/components/shared/DocxView";
import { invalidateDocxBytes } from "@/hooks/useFetchDocxBytes";
import { invalidatePdfBytes } from "@/hooks/useFetchPdfBytes";

/**
 * The flat view contract DocPanel renders. Inspector panes and the shell-less
 * `DocViewModal` build one of these from their own payloads; it is the document
 * viewer's prop shape, not the inspector tab model (see `InspectorTab`).
 */
export interface DocPanelTab {
  documentId: string;
  filename: string;
  versionId?: string;
  versionNumber?: number | null;
  kind: "document" | "citation" | "edit";
  citation?: CitationObject;
  edit?: EditAnnotation;
  warning?: string;
}

/**
 * Screen composition — Document viewer
 *
 * Hero zone: rendered document body (PDF canvas or DOCX prose, full available
 *   height below the header).
 * Scanning regions (compact): header metadata row, zoom toolbar.
 * Focus regions (airy): document body (`flex-1`, unclipped scroll area),
 *   citation blockquote.
 */
interface DocPanelProps {
  tab: DocPanelTab;
  /** When provided, renders a close button in the header (used by the modal viewer; inspector panes close via the rail). */
  onClose?: () => void;
}

function editRowToAnnotation(row: EditRow): EditAnnotation {
  return {
    kind: "edit" as const,
    edit_id: row.edit_id,
    document_id: row.document_id,
    version_id: row.version_id,
    version_number: row.version_number ?? null,
    change_id: row.change_id,
    status: row.status as "pending" | "accepted" | "rejected",
    deleted_text: row.deleted_text,
    inserted_text: row.inserted_text,
    context_before: row.context_before ?? undefined,
    context_after: row.context_after ?? undefined,
  };
}

export function DocPanel({ tab, onClose }: DocPanelProps) {
  const isDocx = /\.docx?$/i.test(tab.filename);
  const [resolvingIds, setResolvingIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<"accept" | "reject" | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const { data: urlData } = useQuery({
    queryKey: ["docUrl", tab.documentId, tab.versionId],
    queryFn: () => api.documents.url(tab.documentId, tab.versionId),
    staleTime: 50 * 60_000, // 50 min — 10 min headroom before the 1-hour signed-URL TTL
  });
  const downloadUrl = urlData?.url ?? null;

  const { data: editsData } = useQuery({
    queryKey: ["pendingEdits", tab.documentId],
    queryFn: () => api.documents.listEdits(tab.documentId, "pending"),
    enabled: isDocx,
    select: (result) => result.edits.map(editRowToAnnotation),
  });
  // editsData is undefined when disabled (non-DOCX); stable empty array via useMemo satisfies exhaustive-deps.
  const pendingEdits = useMemo(() => editsData ?? [], [editsData]);

  const resolveEdit = useCallback(
    async (annotation: EditAnnotation, action: "accept" | "reject") => {
      if (resolvingIds.has(annotation.edit_id)) return;
      setResolvingIds((prev) => new Set(prev).add(annotation.edit_id));
      setEditError(null);
      try {
        if (action === "accept") {
          await api.documents.acceptEdit(annotation.document_id, annotation.edit_id);
        } else {
          await api.documents.rejectEdit(annotation.document_id, annotation.edit_id);
        }
        // invalidateDocxBytes also invalidates ['pendingEdits', documentId] — no manual list splice needed.
        invalidateDocxBytes(annotation.document_id);
        invalidatePdfBytes(annotation.document_id);
      } catch (err) {
        setEditError(err instanceof Error ? err.message : "Could not resolve edit.");
      } finally {
        setResolvingIds((prev) => {
          const next = new Set(prev);
          next.delete(annotation.edit_id);
          return next;
        });
      }
    },
    [resolvingIds],
  );

  const resolveAll = useCallback(
    async (action: "accept" | "reject") => {
      if (bulkAction) return;
      setBulkAction(action);
      setEditError(null);
      const toResolve = [...pendingEdits];
      const resolved: string[] = [];
      try {
        for (const annotation of toResolve) {
          if (action === "accept") {
            await api.documents.acceptEdit(annotation.document_id, annotation.edit_id);
          } else {
            await api.documents.rejectEdit(annotation.document_id, annotation.edit_id);
          }
          resolved.push(annotation.edit_id);
        }
        invalidateDocxBytes(tab.documentId);
        invalidatePdfBytes(tab.documentId);
      } catch (err) {
        if (resolved.length > 0) {
          invalidateDocxBytes(tab.documentId);
          invalidatePdfBytes(tab.documentId);
        }
        const total = toResolve.length;
        const msg = resolved.length > 0
          ? `Resolved ${resolved.length} of ${total} edits — could not resolve the rest.`
          : "Could not resolve edits.";
        setEditError(err instanceof Error ? `${msg} ${err.message}` : msg);
      } finally {
        setBulkAction(null);
      }
    },
    [bulkAction, pendingEdits, tab.documentId],
  );

  const hasPending = pendingEdits.length > 0;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <header className="border-b bg-card px-4 py-3">
        <div className="flex items-start gap-3">
          <FileText className="mt-1 size-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold" title={displayDocumentName(tab.filename)}>
              {displayDocumentName(tab.filename)}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {tab.versionNumber ? <Badge variant="muted">V{tab.versionNumber}</Badge> : null}
              {tab.kind === "citation" && tab.citation?.page ? <span>Page {String(tab.citation.page)}</span> : null}
              {tab.kind === "edit" ? <span>Tracked change</span> : null}
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" disabled={!downloadUrl} asChild={Boolean(downloadUrl)}>
            {downloadUrl ? (
              <a href={downloadUrl} target="_blank" rel="noreferrer">
                <Download className="size-4" />
                Download
              </a>
            ) : (
              <>
                <Download className="size-4" />
                Download
              </>
            )}
          </Button>
          {onClose ? (
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close document viewer">
              <X className="size-4" />
            </Button>
          ) : null}
        </div>
        {tab.kind === "citation" && tab.citation?.quote ? <blockquote className="mt-3 rounded-md bg-muted p-3 text-xs leading-5 text-muted-foreground">{tab.citation.quote.replaceAll("[[PAGE_BREAK]]", " ")}</blockquote> : null}
        {tab.warning ? <ErrorBanner message={tab.warning} className="mt-2 p-2 text-xs [&_svg]:size-3" /> : null}
        {editError ? <p className="mt-2 text-xs text-destructive">{editError}</p> : null}
        {isDocx && hasPending ? (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">{pendingEdits.length} pending edit{pendingEdits.length === 1 ? "" : "s"}</span>
              {pendingEdits.length > 1 ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    disabled={Boolean(bulkAction)}
                    onClick={() => void resolveAll("accept")}
                  >
                    {bulkAction === "accept" ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                    Accept all
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={Boolean(bulkAction)}
                    onClick={() => void resolveAll("reject")}
                  >
                    {bulkAction === "reject" ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
                    Reject all
                  </Button>
                </>
              ) : null}
            </div>
            {pendingEdits.map((edit) => (
              <div key={edit.edit_id} className="flex items-center gap-2 rounded-md border bg-muted/30 p-2 text-sm">
                <div className="min-w-0 flex-1 space-y-0.5">
                  {edit.deleted_text ? <p className="text-xs text-deletion line-through decoration-deletion">{edit.deleted_text}</p> : null}
                  {edit.inserted_text ? <p className="text-xs text-insertion underline decoration-insertion underline-offset-2">{edit.inserted_text}</p> : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-7"
                  disabled={resolvingIds.has(edit.edit_id) || Boolean(bulkAction)}
                  onClick={() => void resolveEdit(edit, "reject")}
                  aria-label="Reject edit"
                >
                  {resolvingIds.has(edit.edit_id) ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  className="size-7"
                  disabled={resolvingIds.has(edit.edit_id) || Boolean(bulkAction)}
                  onClick={() => void resolveEdit(edit, "accept")}
                  aria-label="Accept edit"
                >
                  {resolvingIds.has(edit.edit_id) ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
                </Button>
              </div>
            ))}
          </div>
        ) : null}
      </header>
      <div className="min-h-0 min-w-0 flex-1">{isDocx ? <DocxView documentId={tab.documentId} versionId={tab.versionId} filename={tab.filename} documentMode={tab.kind === "document" ? "editing" : "suggesting"} citation={tab.citation} edit={tab.edit} /> : <DocView documentId={tab.documentId} versionId={tab.versionId} citation={tab.citation} />}</div>
    </div>
  );
}
